import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@supabase/auth-helpers-react';
import { useQuery } from '@tanstack/react-query';
import countriesData from '../list_data/countries_en.json';
import ModernCombobox from '../components/ModernCombobox';
import { getFriendships, getGlobalLeaderboard, getPublicSetting } from '../api/supabaseAPI';
import '../styles/Leaderboard.css';
import '../styles/Introduction.css';

const R2_PUBLIC_BASE_URL_ENV = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');
const DEFAULT_AVATAR = '/Resources/imagine/default_avatar/default_avatar.jpeg';

const getLevelInfo = (totalXpRaw) => {
  const totalXp = Math.max(0, Number(totalXpRaw) || 0);

  let level = 1;
  let levelStartXp = 0;
  let xpToNext = 50;

  while (totalXp >= levelStartXp + xpToNext) {
    levelStartXp += xpToNext;
    level += 1;
    xpToNext = 50 * level;
  }

  const xpIntoLevel = totalXp - levelStartXp;
  const progress = xpToNext > 0 ? Math.min(100, Math.max(0, (xpIntoLevel / xpToNext) * 100)) : 0;

  return {
    level,
    xpIntoLevel,
    xpToNext,
    progress,
  };
};

const Leaderboard = () => {
  const { t } = useTranslation();
  const session = useSession();
  const currentUserId = session?.user?.id || '';
  const [activeTab, setActiveTab] = useState('global');
  const [selectedCountry, setSelectedCountry] = useState('all');

  const countryOptions = useMemo(() => {
    const allOption = { label: t('leaderboardPage.allCountries'), value: 'all' };
    const mappedCountries = countriesData.map((country) => ({
      label: country.name,
      value: country.code,
    }));

    return [allOption, ...mappedCountries];
  }, [t]);

  const countryNameMap = useMemo(
    () => new Map(countriesData.map((country) => [country.code, country.name])),
    []
  );

  const { data: globalLeaderboard = [], isLoading: isLoadingGlobal, error: globalError } = useQuery({
    queryKey: ['leaderboard', 'global-xp'],
    queryFn: getGlobalLeaderboard,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
  });

  const { data: friendsLeaderboard = [], isLoading: isLoadingFriends, error: friendsError } = useQuery({
    queryKey: ['leaderboard', 'friends-xp', currentUserId],
    queryFn: () => getFriendships(currentUserId),
    enabled: !!currentUserId,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
  });

  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  const publicBaseUrl = useMemo(() => {
    const rawBaseUrl = R2_PUBLIC_BASE_URL_ENV || r2PublicBaseUrl || '';
    return rawBaseUrl ? rawBaseUrl.replace(/\/$/, '') : '';
  }, [r2PublicBaseUrl]);

  const activeLeaderboard = activeTab === 'friends' ? friendsLeaderboard : globalLeaderboard;
  const isLoading = activeTab === 'friends' ? isLoadingFriends : isLoadingGlobal;
  const error = activeTab === 'friends' ? friendsError : globalError;

  const leaderboardRows = useMemo(() => {
    const normalizedRows = activeLeaderboard.map((user) => ({
      ...getLevelInfo(user.total_xp),
      id: user.id,
      username: user.username || t('leaderboardPage.unknownUser'),
      avatar: user.avatar || '',
      countryCode: user.country || '',
      countryName: countryNameMap.get(user.country || '') || user.country || t('leaderboardPage.unknownCountry'),
      score: Number(user.total_xp) || 0,
      isCurrentUser: user.id === currentUserId,
    }));

    const filteredRows = selectedCountry === 'all'
      ? normalizedRows
      : normalizedRows.filter((user) => user.countryCode === selectedCountry);

    return [...filteredRows]
      .sort((left, right) => right.score - left.score || left.username.localeCompare(right.username))
      .map((user, index) => ({
        ...user,
        rank: index + 1,
      }));
  }, [activeLeaderboard, countryNameMap, currentUserId, selectedCountry, t]);

  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return DEFAULT_AVATAR;
    if (/^https?:\/\//i.test(avatarPath)) return avatarPath;
    if (!publicBaseUrl) return avatarPath;
    return `${publicBaseUrl}/${avatarPath}`;
  };

  const emptyStateMessage = activeTab === 'friends'
    ? t('leaderboardPage.emptyFriends')
    : t('leaderboardPage.emptyGlobal');

  return (
    <main className="leaderboard-container">
      <h1 className="page-title">{t('leaderboardPage.title', 'Leaderboard')}</h1>
      <header className="leaderboard-header">
        <div className="leaderboard-title-group">
          <p className="leaderboard-subtitle">
            {t('leaderboardPage.subtitle', 'Rankings based on total XP earned.')}
          </p>
        </div>
      </header>

      <div className="leaderboard-filters">
        <ModernCombobox
          placeholder={t('leaderboardPage.countryFilter', 'Select Country')}
          options={countryOptions}
          value={selectedCountry}
          onChange={setSelectedCountry}
          allowTyping={true}
        />
      </div>

      <div className="leaderboard-content">
        <div className="leaderboard-tabs">
          <button
            type="button"
            className={`leaderboard-tab ${activeTab === 'global' ? 'active' : ''}`}
            onClick={() => setActiveTab('global')}
          >
            {t('leaderboardPage.globalRanking', 'Global Ranking')}
          </button>
          <button
            type="button"
            className={`leaderboard-tab ${activeTab === 'friends' ? 'active' : ''}`}
            onClick={() => setActiveTab('friends')}
          >
            {t('leaderboardPage.friendsRanking', 'Friends Ranking')}
          </button>
        </div>

        <div className="leaderboard-list">
          <div className="leaderboard-header-row">
            <span>{t('leaderboardPage.rank', 'Rank')}</span>
            <span>{t('leaderboardPage.user', 'User')}</span>
            <span className="leaderboard-score-header">{t('leaderboardPage.level', 'Level')}</span>
          </div>

          {isLoading && <div className="leaderboard-state">{t('common.loading', 'Loading...')}</div>}

          {!isLoading && error && (
            <div className="leaderboard-state leaderboard-state-error">
              {t('leaderboardPage.loadError', 'Unable to load leaderboard right now.')}
            </div>
          )}

          {!isLoading && !error && leaderboardRows.length === 0 && (
            <div className="leaderboard-state">{emptyStateMessage}</div>
          )}

          {!isLoading && !error && leaderboardRows.map((user) => (
            <div
              key={user.id}
              className={`leaderboard-row ${user.rank <= 3 ? 'top-rank' : ''} ${user.isCurrentUser ? 'highlight' : ''}`}
            >
              <div className="rank-col">
                <div className={`rank-cell rank-${user.rank}`}>
                  {user.rank}
                </div>
              </div>

              <div className="user-cell">
                <img
                  src={getAvatarUrl(user.avatar)}
                  alt={user.username}
                  className="user-avatar"
                  onError={(event) => {
                    event.currentTarget.src = DEFAULT_AVATAR;
                  }}
                />
                <div className="user-info">
                  <span className="user-name">{user.username}</span>
                  <span className="user-country">{user.countryName}</span>
                </div>
              </div>

              <div className="score-cell">
                <div className="score-topline">
                  <span className="score-primary">{t('leaderboardPage.levelShort', { level: user.level })}</span>
                  <span className="score-label">
                    {t('leaderboardPage.xpProgress', {
                      current: user.xpIntoLevel.toLocaleString(),
                      total: user.xpToNext.toLocaleString(),
                    })}
                  </span>
                </div>
                <progress
                  className="leaderboard-progress-bar"
                  max={100}
                  value={user.progress}
                  aria-label={t('leaderboardPage.levelProgressAria', { username: user.username })}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};

export default Leaderboard;
